let browserPrintLoader = null;
const templateCache = new Map();

// The Browser Print SDK must be served locally — it is NOT served by the Browser Print service.
// The file is hosted from /public/BrowserPrint-3.1.250.min.js
const BROWSER_PRINT_SDK_URL = '/BrowserPrint-3.1.250.min.js';

function sanitizeLabelValue(value, maxLen = 25) {
  const text = String(value || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/[\^~]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

  if (!maxLen || text.length <= maxLen) return text;
  return text.slice(0, maxLen).trim();
}

async function loadBrowserPrint() {
  if (typeof window === 'undefined') {
    throw new Error('Browser print is only available in a browser environment.');
  }

  if (window.BrowserPrint) return window.BrowserPrint;

  if (browserPrintLoader) {
    await browserPrintLoader;
    if (!window.BrowserPrint) {
      throw new Error('Browser Print SDK did not initialize.');
    }
    return window.BrowserPrint;
  }

  browserPrintLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-zebra-browser-print]`);
    if (existing) {
      if (window.BrowserPrint) { resolve(); return; }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed loading Browser Print SDK')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = BROWSER_PRINT_SDK_URL;
    script.async = true;
    script.dataset.zebraBrowserPrint = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(
      `Unable to load Browser Print SDK from ${BROWSER_PRINT_SDK_URL}. ` +
      `Ensure the file exists at public/BrowserPrint-3.1.250.min.js.`
    ));
    document.head.appendChild(script);
  });

  await browserPrintLoader;

  if (!window.BrowserPrint) {
    throw new Error('Browser Print SDK loaded but did not initialize window.BrowserPrint.');
  }

  return window.BrowserPrint;
}

async function getDefaultPrinter() {
  const BrowserPrint = await loadBrowserPrint();

  return new Promise((resolve, reject) => {
    BrowserPrint.getDefaultDevice(
      'printer',
      (device) => {
        if (!device) {
          reject(new Error('No default printer found. Set your Zebra ZD421 as default printer.'));
          return;
        }
        resolve(device);
      },
      (err) => {
        const message = typeof err === 'string' ? err : (err?.message || 'Failed to discover default printer.');
        reject(new Error(message));
      }
    );
  });
}

function resolveBadgeTemplatePath(templateName) {
  const rawName = String(templateName || '').trim() || 'vendor-badge-template';
  const normalizedName = rawName.endsWith('.zpl') ? rawName : `${rawName}.zpl`;
  return `/zpl/${normalizedName}`;
}

async function loadBadgeTemplate(templateName) {
  const templatePath = resolveBadgeTemplatePath(templateName);
  if (templateCache.has(templatePath)) return templateCache.get(templatePath);

  const response = await fetch(templatePath, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Unable to load ZPL template file from ${templatePath}`);
  }

  const templateText = await response.text();
  templateCache.set(templatePath, templateText);
  return templateText;
}

function renderBadgeZpl(template, attendeeName, vendorName) {
  const replacements = {
    attendeeName: sanitizeLabelValue(attendeeName, 25),
    vendorName: sanitizeLabelValue(vendorName, 25),
  };

  return Object.entries(replacements).reduce((output, [token, value]) => {
    return output
      .replaceAll(`{{${token}}}`, value)
      .replaceAll(`\\${token}\\`, value)
      .replaceAll(token, value);
  }, template);
}

async function readPrinterResponse(printer) {
  return new Promise((resolve) => {
    printer.read(
      (response) => resolve(response || '(empty)'),
      () => resolve('(no response)')
    );
  });
}

async function sendRawZplToDefaultPrinter(zplPayload) {
  const printer = await getDefaultPrinter();

  console.log('[ZebraPrint] Printer device:', printer?.name, printer?.uid);
  console.log('[ZebraPrint] ZPL payload:\n', zplPayload);

  await new Promise((resolve, reject) => {
    printer.send(
      zplPayload,
      async () => {
        // Read any status/error the printer sends back after the job
        const response = await readPrinterResponse(printer);
        console.log('[ZebraPrint] Printer response after write:', response);
        resolve();
      },
      (err) => {
        const message = typeof err === 'string' ? err : (err?.message || 'Failed to send print payload.');
        reject(new Error(message));
      }
    );
  });
}

export async function printVendorBadgesWithTemplate(items, vendorName, templateName) {
  const rows = Array.isArray(items) ? items : [];
  if (rows.length === 0) {
    throw new Error('No attendees selected for printing.');
  }

  const template = await loadBadgeTemplate(templateName);
  console.log('[ZebraPrint] Template loaded:', template.substring(0, 80));

  const zplPayload = rows
    .map((row) => {
      const name = `${row?.firstName || row?.name || ''} ${row?.lastName || ''}`.trim();
      console.log('[ZebraPrint] Rendering badge for:', name, '/', vendorName);
      return renderBadgeZpl(template, name, vendorName);
    })
    .join('\n');

  await sendRawZplToDefaultPrinter(zplPayload);

  return {
    printedCount: rows.length,
  };
}
